import React, { useEffect, useMemo, useState } from 'react';
import { Award, Lock, ArrowUpRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { LocalLink, useLang } from '../i18n/useLocalizedNav';
import Seo from '../components/Seo';
import Stamp from '../components/stamps/Stamp';
import StampDetailsModal from '../components/stamps/StampDetailsModal';
import TierCrest from '../components/stamps/TierCrest';
import CountUp from '../components/CountUp';
import { getRank, TIERS } from '../utils/tiers';
import { getStamps, getStampById } from '../content/catalog';

type StampLocation = {
  id: string;
  slug: string;
  name: string;
  region: string | null;
};

type StampData = {
  id: string;
  name: string;
  multiplier: number | null;
  earnedAt: string | null;
  locations: StampLocation[];
};

// Stamp medallion + the venues where it can be earned (linked). When
// earned, clicking the medallion opens the per-claim history modal.
const StampCard: React.FC<{
  stamp: StampData;
  earned: boolean;
  onOpen?: () => void;
}> = ({ stamp, earned, onOpen }) => {
  const { t } = useTranslation();
  // Narrow phones can't fit two 200px medallions side-by-side with the
  // x2/x3 badge poking out — shrink to 150px below the sm: breakpoint.
  const [isSmUp, setIsSmUp] = useState(
    typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 640px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 640px)');
    const onChange = () => setIsSmUp(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const size = isSmUp ? 200 : 150;

  return (
  <div
    className="flex flex-col items-center gap-3"
    style={{ width: size }}
  >
    <Stamp
      name={stamp.name}
      earned={earned}
      multiplier={earned ? stamp.multiplier : undefined}
      onClick={earned ? onOpen : undefined}
      size={size}
    />
    {stamp.locations.length > 0 && (
      <div className="text-center space-y-1">
        <p className="font-heading font-bold text-[10px] tracking-[0.12em] text-warm-500 uppercase">
          {earned ? t('ms_earned_at') : t('ms_where_find')}
        </p>
        <ul className="space-y-1">
          {stamp.locations.map((loc) => (
            <li key={loc.id}>
              <LocalLink
                to={`/locations/${loc.slug}`}
                className="group inline-flex items-center gap-1 text-sm text-brand-accent hover:text-warm-800 transition-colors"
              >
                <span className="underline decoration-brand-accent/40 decoration-1 underline-offset-2 group-hover:decoration-warm-800/60">
                  {loc.name}
                </span>
                <ArrowUpRight className="w-3 h-3 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
              </LocalLink>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
  );
};

// Stable, unique passport number derived from the user id.
const passportNo = (userId: string): string => {
  let h = 0;
  for (let i = 0; i < userId.length; i++) {
    h = (h * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return `00-${(h % 10000).toString().padStart(4, '0')}`;
};

const issuedLabel = (locale: string) =>
  new Date()
    .toLocaleDateString(locale, { month: 'short', year: 'numeric' })
    .toUpperCase();

const MyStamps: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const lang = useLang();

  // Localized catalog of all stamps (all locked). This is what the
  // logged-out / prerendered view renders — no API call.
  const catalogStamps = useMemo<StampData[]>(
    () =>
      getStamps(lang).map((s) => ({
        id: s.id,
        name: s.name,
        multiplier: null,
        earnedAt: null,
        locations: s.locations,
      })),
    [lang],
  );

  // Only the logged-in earned state comes from the API; null until
  // loaded. Logged-out / prerendered view uses the static catalog.
  const [earnedStamps, setEarnedStamps] = useState<StampData[] | null>(
    null,
  );
  // Loading only matters for the authenticated fetch; starts true iff
  // there's a logged-in user to fetch for (set outside the effect body).
  const [isLoading, setIsLoading] = useState<boolean>(!!user);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/stamps/my');
        if (!alive) return;
        if (Array.isArray(data)) {
          // The API returns EVERY stamp (visible or not) with the user's
          // earned/multiplier state. Visibility is a frontend concern, so we
          // filter here: keep a stamp only if it's tied to a visible venue
          // (getStamps is already visibility-filtered) OR the user has earned
          // it — so hiding a venue drops its stamp from the locked catalog
          // while never taking away a stamp someone already collected.
          const visibleIds = new Set(getStamps(lang).map((s) => s.id));
          setEarnedStamps(
            data
              .filter(
                (s: StampData) => visibleIds.has(s.id) || s.multiplier !== null,
              )
              // Overlay localized names/venues. Resolve via getStampById
              // (unfiltered) so an earned stamp from a now-hidden venue still
              // gets its name — falling back to the id, never undefined
              // (which would crash the medallion).
              .map((s: StampData) => {
                const tr = getStampById(lang, s.id);
                return tr
                  ? { ...s, name: tr.name, locations: tr.locations }
                  : { ...s, name: s.name ?? s.id, locations: s.locations ?? [] };
              }),
          );
        }
      } catch {
        console.error('Failed to fetch stamps');
      } finally {
        if (alive) setIsLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user, lang]);

  // Logged-out (or before fetch) → static localized catalog. The earned
  // state only applies while authenticated.
  const stamps = user && earnedStamps ? earnedStamps : catalogStamps;

  // Open the per-claim history modal for an earned stamp.
  const [openStampId, setOpenStampId] = useState<string | null>(null);
  const openStamp = useMemo(
    () => stamps.find((s) => s.id === openStampId) ?? null,
    [openStampId, stamps],
  );

  // Newest claim first. `earnedAt` is the most recent claim's timestamp
  // (server returns it that way) — stamps with no timestamp sort last.
  const earned = useMemo(
    () =>
      stamps
        .filter((s) => s.multiplier !== null)
        .slice()
        .sort((a, b) => {
          const ta = a.earnedAt ? Date.parse(a.earnedAt) : 0;
          const tb = b.earnedAt ? Date.parse(b.earnedAt) : 0;
          return tb - ta;
        }),
    [stamps],
  );
  const locked = useMemo(
    () => stamps.filter((s) => s.multiplier === null),
    [stamps],
  );

  const uniqueStamps = earned.length;
  // "Collected" = total verifications (sum of multipliers).
  const collected = earned.reduce((sum, s) => sum + (s.multiplier ?? 1), 0);
  const bestMultiplier = earned.reduce(
    (max, s) => Math.max(max, s.multiplier ?? 1),
    0,
  );

  const rank = getRank(uniqueStamps);

  if (!user) {
    return (
      <div className="space-y-8">
        <Seo
          titleKey="seo.my_stamps_title"
          descriptionKey="seo.my_stamps_description"
        />
        <div className="bg-brand-secondary text-white p-8 rounded-3xl shadow-xl relative overflow-hidden">
          <div className="relative z-10 space-y-3">
            <p className="text-brand-primary font-bold uppercase tracking-widest text-xs">
              {t('ms_the_collection')}
            </p>
            <h1 className="font-display text-4xl text-white">
              {t('ms_hero_title')}
            </h1>
            <p className="text-warm-100/80 max-w-lg">
              {t('ms_hero_sub')}
            </p>
            <LocalLink
              to="/login"
              className="inline-flex items-center gap-2 mt-2 px-6 py-3 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl shadow-lg hover:bg-brand-accent transition-all"
            >
              <Lock className="w-4 h-4" />
              {t('ms_login_progress')}
            </LocalLink>
          </div>
          <Award className="absolute -right-8 -bottom-8 w-48 h-48 text-white/5 rotate-12" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 sm:gap-x-6 gap-y-8 sm:gap-y-10 justify-items-center items-start">
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="w-[200px] h-[200px] bg-warm-200 rounded-full animate-pulse"
                />
              ))
            : stamps.map((stamp) => (
                <StampCard key={stamp.id} stamp={stamp} earned={false} />
              ))}
        </div>
      </div>
    );
  }

  const displayName = user.fullName || user.email;
  const [firstName, ...restName] = displayName.split(/\s+/);

  return (
    <div className="space-y-8">
      <Seo
        titleKey="seo.my_stamps_title"
        descriptionKey="seo.my_stamps_description"
      />
      {/* Passport header */}
      <div className="bg-brand-secondary text-white p-8 md:p-10 rounded-3xl shadow-xl relative overflow-hidden">
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Identity + rank */}
          <div className="space-y-5">
            <p className="font-heading font-bold text-xs tracking-[0.25em] text-brand-primary uppercase">
              {t('ms_official_no', { no: passportNo(user.id) })}
            </p>
            <h1 className="font-display text-4xl md:text-5xl text-white leading-tight">
              {firstName}{' '}
              <span className="italic text-brand-primary">
                {restName.join(' ')}
              </span>
            </h1>
            <p className="text-warm-300 text-sm">
              {t('ms_issued', { date: issuedLabel(lang) })}
            </p>

            <div className="flex items-center gap-4 pt-2">
              <TierCrest
                color={rank.tier.crest}
                label={rank.tier.short}
                className="w-14 h-16 shrink-0"
              />
              <div className="flex-1 space-y-2">
                <p className="font-display text-2xl text-brand-primary">
                  {t('ms_rank', { tier: t(`tier_${rank.tier.short}`) })}
                </p>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-primary rounded-full transition-all"
                    style={{ width: `${rank.progress * 100}%` }}
                  />
                </div>
                <p className="text-warm-300 text-xs">
                  {rank.nextTier
                    ? t('ms_until_next', {
                        count: rank.toNext,
                        tier: t(`tier_${rank.nextTier.short}`),
                      })
                    : t('ms_top_reached')}
                </p>
              </div>
            </div>
          </div>

          {/* Stat tiles */}
          <div className="space-y-4 self-start min-w-0">
            <div className="grid grid-cols-3 gap-2 sm:gap-3">
              {[
                { value: collected, label: t('ms_stat_collected'), prefix: '' },
                { value: uniqueStamps, label: t('ms_stat_unique'), prefix: '' },
                { value: bestMultiplier, label: t('ms_stat_best'), prefix: '×' },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="bg-white/5 rounded-2xl p-3 sm:p-5 space-y-1 min-w-0"
                >
                  <p className="font-display text-2xl sm:text-3xl text-white">
                    <CountUp value={stat.value} prefix={stat.prefix} />
                  </p>
                  <p className="font-heading font-bold text-[9px] sm:text-[10px] tracking-[0.06em] sm:tracking-[0.12em] text-warm-300 uppercase leading-tight break-words">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>

            {collected > 0 && (
              <div className="bg-white/5 rounded-2xl p-4 space-y-3 min-w-0">
                <p className="font-heading font-bold text-[10px] tracking-[0.12em] text-warm-300 uppercase">
                  {t('ms_tier_ladder')}
                </p>
                <div className="grid grid-cols-5 gap-1 sm:gap-2">
                  {TIERS.map((tier, i) => {
                    const active = tier.short === rank.tier.short;
                    const next = TIERS[i + 1];
                    return (
                      <div
                        key={tier.short}
                        className={`min-w-0 flex flex-col items-center text-center gap-1.5 rounded-xl py-2 px-0.5 transition-colors ${
                          active
                            ? 'bg-brand-primary/15 ring-1 ring-brand-primary/40'
                            : 'opacity-50'
                        }`}
                        title={`${t(`tier_${tier.short}`)} · ${t('tier_range_stamps', { range: tier.stampRange })}`}
                      >
                        <TierCrest
                          color={tier.crest}
                          label={tier.short}
                          className="w-7 h-8"
                        />
                        <p
                          className={`w-full truncate font-heading font-bold text-[8px] sm:text-[9px] tracking-[0.05em] sm:tracking-[0.1em] uppercase leading-tight ${
                            active ? 'text-brand-primary' : 'text-warm-500'
                          }`}
                        >
                          {tier.short}
                        </p>
                        <p className="text-[9px] text-warm-500 leading-tight whitespace-nowrap">
                          {tier.min}
                          {next ? `–${next.min - 1}` : '+'}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {collected === 0 && (
              <div className="bg-brand-primary/10 border border-brand-primary/20 rounded-2xl p-5 flex items-center justify-between gap-4">
                <p className="text-warm-100/90 text-sm leading-relaxed">
                  {t('ms_empty_1')}{' '}
                  <span className="text-brand-primary font-bold">
                    {t('ms_empty_2')}
                  </span>
                </p>
                <LocalLink
                  to="/locations"
                  className="shrink-0 px-5 py-2.5 bg-brand-primary text-brand-secondary font-heading font-bold text-sm rounded-xl hover:bg-brand-accent transition-all"
                >
                  {t('ms_explore')}
                </LocalLink>
              </div>
            )}
          </div>
        </div>
        <Award className="absolute -right-10 -bottom-10 w-56 h-56 text-white/5 rotate-12" />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 justify-items-center">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="w-[200px] h-[200px] bg-warm-200 rounded-full animate-pulse"
            />
          ))}
        </div>
      ) : (
        <>
          {earned.length > 0 && (
            <section className="space-y-5">
              <p className="font-heading font-bold text-sm tracking-[0.2em] text-brand-accent uppercase">
                {t('ms_earned_count', { count: earned.length })}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 sm:gap-x-6 gap-y-8 sm:gap-y-10 justify-items-center items-start">
                {earned.map((stamp) => (
                  <StampCard
                    key={stamp.id}
                    stamp={stamp}
                    earned
                    onOpen={() => setOpenStampId(stamp.id)}
                  />
                ))}
              </div>
            </section>
          )}

          {locked.length > 0 && (
            <section className="space-y-5">
              <p className="font-heading font-bold text-sm tracking-[0.2em] text-warm-500 uppercase">
                {t('ms_locked_count', { count: locked.length })}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 sm:gap-x-6 gap-y-8 sm:gap-y-10 justify-items-center items-start">
                {locked.map((stamp) => (
                  <StampCard key={stamp.id} stamp={stamp} earned={false} />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {openStamp && (
        <StampDetailsModal
          stamp={openStamp}
          onClose={() => setOpenStampId(null)}
        />
      )}
    </div>
  );
};

export default MyStamps;
